import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CalendarSettings, DEFAULT_CALENDAR_SETTINGS } from '../../models/calendar.types';

export interface CalendarSettingsDialogData {
  settings?: CalendarSettings;
}

@Component({
  selector: 'aura-calendar-settings-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    FormsModule,
  ],
  templateUrl: './calendar-settings-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarSettingsDialogComponent {
  readonly dialogRef = inject(MatDialogRef<CalendarSettingsDialogComponent>);
  readonly data = inject<CalendarSettingsDialogData>(MAT_DIALOG_DATA, { optional: true });

  private readonly initialSettings = this.data?.settings ?? DEFAULT_CALENDAR_SETTINGS;

  readonly showWeekends = signal<boolean>(this.initialSettings.showWeekends ?? true);
  readonly firstDayOfWeek = signal<1 | 0>(this.initialSettings.firstDayOfWeek ?? 1);
  readonly weekStartHour = signal<number>(this.initialSettings.weekStartHour ?? 6);
  readonly weekEndHour = signal<number>(this.initialSettings.weekEndHour ?? 22);

  readonly availableStartHours = [5, 6, 7, 8, 9, 10, 11, 12];
  readonly availableEndHours = [16, 17, 18, 19, 20, 21, 22, 23];

  readonly isValidHours = computed(() => this.weekStartHour() < this.weekEndHour());

  formatHourLabel(hour: number): string {
    return `${String(hour).padStart(2, '0')}:00`;
  }

  onSave(): void {
    if (!this.isValidHours()) {
      return;
    }

    const updated: CalendarSettings = {
      showWeekends: this.showWeekends(),
      firstDayOfWeek: this.firstDayOfWeek(),
      weekStartHour: this.weekStartHour(),
      weekEndHour: this.weekEndHour(),
    };

    this.dialogRef.close(updated);
  }
}
